/**
 * The shelf's two filters — KANON `06-katalog-pytan.html` draws them as two labelled chips.
 *
 * Two buttons rather than one funnel, unlike the Ankiety tab: the canon names both narrowings on the
 * surface („Typ odpowiedzi", „Użyte w") and there are only two of them, so nothing is being hidden
 * behind an icon the way four facets were.
 *
 * The answer-type list offers only the types the shelf actually HOLDS. A chip that can only empty
 * the list is a chip nobody wants; `shelfAnswerTypes` is what decides that.
 *
 * `Menu.RadioItem` keeps its popup open on a press by default, which is right for a single-choice
 * picker the trainer may change twice — so the popup is not dismissed here either.
 */

import { Menu } from '@base-ui/react/menu';

import menuStyles from '~/shared/ui/MenuSurface.module.css';

import type { ShelfFacets } from '~/features/survey-question-editor';
import type { TranslateKey } from '../model/surveyList';
import canon from '~/shared/ui/surveyCanon.module.css';

const ANY_TYPE = 'all';
const USAGES: readonly ShelfFacets['usage'][] = ['all', 'used', 'unused'];

export interface ShelfFiltersProps {
  readonly facets: ShelfFacets;
  readonly answerTypes: readonly string[];
  readonly onChange: (facets: ShelfFacets) => void;
  readonly t: TranslateKey;
}

export function ShelfFilters({ facets, answerTypes, onChange, t }: ShelfFiltersProps) {
  const typeLabel =
    facets.answerType === null
      ? t('web:reports.catalog.filters.type')
      : t('web:reports.catalog.filters.typeActive', {
          type: t(`intakeSurvey.answerType.${facets.answerType}`),
        });
  const usageLabel =
    facets.usage === 'all'
      ? t('web:reports.catalog.filters.usage')
      : t(`web:reports.catalog.filters.usage_${facets.usage}`);

  return (
    <>
      <Menu.Root>
        <Menu.Trigger
          className={`${String(canon.chip)} ${String(canon.chipLine)} ${String(canon.chipLg)}`}
        >
          {typeLabel}
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="start">
            <Menu.Popup className={menuStyles.popup}>
              <Menu.RadioGroup
                value={facets.answerType ?? ANY_TYPE}
                onValueChange={(value) => {
                  onChange({ ...facets, answerType: value === ANY_TYPE ? null : String(value) });
                }}
              >
                <Menu.GroupLabel className={menuStyles.groupLabel}>
                  {t('web:reports.catalog.filters.type')}
                </Menu.GroupLabel>
                <Menu.RadioItem className={menuStyles.item} value={ANY_TYPE}>
                  {/* The tick is a second channel beside the `aria-checked` Base UI writes: a
                    * sighted trainer reads the mark, a listener reads the state. */}
                  <span className={menuStyles.mark} aria-hidden="true">
                    {facets.answerType === null ? '✓' : ''}
                  </span>
                  <span className={menuStyles.itemLabel}>
                    {t('web:reports.catalog.filters.typeAll')}
                  </span>
                </Menu.RadioItem>
                {answerTypes.map((type) => (
                  <Menu.RadioItem key={type} className={menuStyles.item} value={type}>
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.answerType === type ? '✓' : ''}
                    </span>
                    <span className={menuStyles.itemLabel}>
                      {t(`intakeSurvey.answerType.${type}`)}
                    </span>
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>

      <Menu.Root>
        <Menu.Trigger
          className={`${String(canon.chip)} ${String(canon.chipLine)} ${String(canon.chipLg)}`}
        >
          {usageLabel}
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="start">
            <Menu.Popup className={menuStyles.popup}>
              <Menu.RadioGroup
                value={facets.usage}
                onValueChange={(value) => {
                  onChange({ ...facets, usage: value as ShelfFacets['usage'] });
                }}
              >
                <Menu.GroupLabel className={menuStyles.groupLabel}>
                  {t('web:reports.catalog.filters.usage')}
                </Menu.GroupLabel>
                {USAGES.map((usage) => (
                  <Menu.RadioItem key={usage} className={menuStyles.item} value={usage}>
                    <span className={menuStyles.mark} aria-hidden="true">
                      {facets.usage === usage ? '✓' : ''}
                    </span>
                    <span className={menuStyles.itemLabel}>
                      {t(`web:reports.catalog.filters.usage_${usage}`)}
                    </span>
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </>
  );
}
